import { datadogLogs } from "@datadog/browser-logs";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { generateCode } from "@examspark/common";
import { useVolatileRef } from "@examspark/common-ui";
import { AnyObject } from "@examspark/types";

import { useAppContext } from "../providers/AppContext";

const getInterestingGlobals = () => {
  const interestingKeys = Object.keys(window).filter((key) => {
    if (key === "localStorage" || key === "sessionStorage") return true;
    if (typeof window[key as keyof typeof window] === "function") return false;

    const propDesc = Object.getOwnPropertyDescriptor(window, key);
    return (
      !!propDesc?.value &&
      !!propDesc.writable &&
      !!propDesc.configurable &&
      !!propDesc.enumerable
    );
  });

  return Object.fromEntries(
    interestingKeys.map((key) => [key, window[key as keyof typeof window]]),
  );
};

export const alertDebugReport = (message: string, state?: AnyObject) => {
  const reportId = logDebugReport(state);

  alert(
    message +
      "\nPlease include this report ID on any support communications: " +
      reportId,
  );
};

export const logDebugReport = (state?: AnyObject) => {
  const reportId = generateCode();

  state = {
    location: window.location.href,
    globals: getInterestingGlobals(),
    ...state,
  };

  datadogLogs.logger.info("Debug Report", {
    reportId,
    state,
  });

  console.log("Debug Report", {
    reportId,
    state,
  });

  return reportId;
};

export const useDebugReporter = () => {
  const [app] = useAppContext();
  const flags = useFlags();

  const stateRef = useVolatileRef({ app, flags });

  const logDebugReportInternal = useCallback(
    (additionalState?: AnyObject) => {
      logDebugReport({
        ...additionalState,
        ...stateRef.current,
      });
    },
    [stateRef],
  );

  const alertDebugReportInternal = useCallback(
    (message: string, additionalState?: AnyObject) => {
      alertDebugReport(message, { ...additionalState, ...stateRef.current });
    },
    [stateRef],
  );

  return {
    logDebugReport: logDebugReportInternal,
    alertDebugReport: alertDebugReportInternal,
  };
};

export const useDebugReporterHotkey = (state?: AnyObject) => {
  const stateRef = useVolatileRef(state);
  const { alertDebugReport } = useDebugReporter();

  const cb = useCallback(() => {
    alertDebugReport("Thanks for submitting a debug report!", stateRef.current);
  }, [alertDebugReport, stateRef]);

  useHotkeys("ctrl+alt+?", cb, { useKey: true });
  useHotkeys("ctrl+alt+shift+/", cb, { useKey: true });
};
